import { useEffect, useMemo, useState } from 'react'

import cn from 'classnames'
import { Tooltip } from 'react-tooltip'

import { SwapButton } from '@src/components/buttons/SwapButton'
import { APP } from '@src/configs/paths'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { MIN_MILES_FOR_MON_PURCHASE } from '@src/constants/miles'
import { StepNames } from '@src/enums'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteResponse } from '@src/types/order'
import { Address } from '@src/types/token'
import { Balance, isFeatureEnabled } from '@src/utils'

interface SwapErrors {
    isInsufficientBalance: boolean
    isQuoteInvalid: boolean
    isBuyOutputLoading: boolean
    // add other booleans if needed
}

interface ISwapActionButtonProps {
    sellData: IExchangeData
    buyData: IExchangeData

    sellAmountString: string

    /** For your aggregator's quote result. */
    quoteResponse: IQuoteResponse

    errors: SwapErrors
    hasInsufficientGas: boolean

    /** If user toggled "custom wallet"? */
    isCustomWallet: boolean
    destinationAddress: Address | undefined

    /** If the user wants to do something on button click. */
    onConfirmExchange: () => void

    /** Gas fee simulation status. */
    gasFeeSimulationInProgress?: boolean
    isQuoteLoading?: boolean
}

/**
 * A wrapper for your <SwapButton> that sets disabled states,
 * passes in the needed props, etc.
 */
export function SwapActionButton(props: ISwapActionButtonProps) {
    const {
        sellData,
        buyData,
        sellAmountString,
        quoteResponse,
        errors,
        hasInsufficientGas,
        isCustomWallet,
        destinationAddress,
        onConfirmExchange,
        gasFeeSimulationInProgress,
        isQuoteLoading,
    } = props

    const { isTransactionCapped, transactionCapRemainingAllowance, transactionCapNextUnlock, transactionCap } =
        useExchangeState()
    const { transactionCapError, isLoadingTransactionCap, milesStatus } = useExchangeDataContext()

    // State to track the countdown timer
    const [timeRemaining, setTimeRemaining] = useState<{ hours: number; minutes: number; seconds: number } | null>(
        transactionCapNextUnlock
    )

    // Check if buy amount exceeds cap
    const buyAmountExceedsCap = useMemo(() => {
        if (transactionCapRemainingAllowance === null) {
            return false
        }

        // Use buy amount instead of sell amount for comparison
        const buyAmount = buyData.value
        return buyAmount > BigInt(transactionCapRemainingAllowance)
    }, [buyData.value, transactionCapRemainingAllowance])

    // Format the remaining transaction cap for display
    const formattedRemainingTransactionCap = useMemo(() => {
        if (transactionCapRemainingAllowance === null) {
            return `0 ${buyData.token.symbol}`
        }

        try {
            const capValue = BigInt(transactionCapRemainingAllowance)
            return `${new Balance(buyData.token)
                .setValue(capValue)
                .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${buyData.token.symbol}`
        } catch (e) {
            return `0 ${buyData.token.symbol}`
        }
    }, [transactionCapRemainingAllowance, buyData.token])

    // Format the total transaction cap for display
    const formattedTransactionCap = useMemo(() => {
        if (transactionCap === null) {
            return `0 ${buyData.token.symbol}`
        }

        try {
            const capValue = BigInt(transactionCap)
            return `${new Balance(buyData.token)
                .setValue(capValue)
                .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${buyData.token.symbol}`
        } catch (e) {
            return `0 ${buyData.token.symbol}`
        }
    }, [transactionCap, buyData.token])

    // Update the timer every second
    useEffect(() => {
        if (isFeatureEnabled('TRANSACTION_CAP')) {
            // Initialize with the value from context
            setTimeRemaining(transactionCapNextUnlock)

            // Only start the timer if we have a next unlock time
            if (!transactionCapNextUnlock) return

            const timer = setInterval(() => {
                setTimeRemaining(prev => {
                    if (!prev) return null

                    let { hours, minutes, seconds } = prev

                    // Decrement seconds
                    seconds--

                    // Handle minute rollover
                    if (seconds < 0) {
                        seconds = 59
                        minutes--
                    }

                    // Handle hour rollover
                    if (minutes < 0) {
                        minutes = 59
                        hours--
                    }

                    // Stop at zero
                    if (hours < 0) {
                        clearInterval(timer)
                        return null
                    }

                    return { hours, minutes, seconds }
                })
            }, 1000)

            return () => clearInterval(timer)
        }
    }, [transactionCapNextUnlock])

    // Format the time until next unlock using the live countdown
    const formatTimeUntilNextUnlock = () => {
        if (!timeRemaining) return ''

        const { hours, minutes, seconds } = timeRemaining
        const parts = []

        if (hours > 0) parts.push(`${hours}h`)
        if (minutes > 0) parts.push(`${minutes}m`)
        if (seconds > 0 && hours === 0) parts.push(`${seconds}s`) // Only show seconds if less than an hour

        return parts.join(' ')
    }

    // Get button text based on transaction cap status
    const getButtonText = () => {
        // If there's an error fetching the cap
        if (transactionCapError) {
            return 'Route Temporarily Disabled'
        }

        // If we're still loading the cap
        if (isLoadingTransactionCap) {
            return 'Checking Cap...'
        }

        // If the cap is reached
        if (isTransactionCapped && timeRemaining) {
            return `Route Disabled (${formatTimeUntilNextUnlock()})`
        }

        // If the amount exceeds the cap
        if (buyAmountExceedsCap && transactionCapRemainingAllowance !== null) {
            return `Exceeds daily limit (${formattedRemainingTransactionCap} left)`
        }

        // If we have cap information but no issues
        if (transactionCapRemainingAllowance !== null) {
            // Show the time until next unlock if available, even when there's still cap remaining
            if (timeRemaining) {
                return `Swap (${formattedRemainingTransactionCap} of daily limit left), resets in ${formatTimeUntilNextUnlock()}`
            }
            return `Swap (${formattedRemainingTransactionCap} of daily limit left)`
        }

        return 'Swap'
    }

    // Get transaction cap message for display above the button
    const getTransactionCapMessage = () => {
        // If there's an error fetching the cap
        if (transactionCapError) {
            return `Route temporarily disabled: ${transactionCapError}`
        }

        // If we're still loading the cap
        if (isLoadingTransactionCap) {
            return 'Checking transaction cap...'
        }

        // If the cap is reached
        if (isTransactionCapped && timeRemaining) {
            return `Daily cap of ${formattedTransactionCap} reached. Next available in ${formatTimeUntilNextUnlock()}`
        }

        // If the amount exceeds the cap
        if (buyAmountExceedsCap && transactionCapRemainingAllowance !== null) {
            if (timeRemaining) {
                return `Amount exceeds remaining daily limit of ${formattedRemainingTransactionCap}. Full limit resets in ${formatTimeUntilNextUnlock()}.`
            }
            return `Amount exceeds remaining daily limit of ${formattedRemainingTransactionCap}. Route limited.`
        }

        // If there's still some cap remaining but not full, show when it will reset
        if (transactionCapRemainingAllowance !== null && timeRemaining) {
            return `Daily limit usage: ${formattedRemainingTransactionCap} remaining. Full limit resets in ${formatTimeUntilNextUnlock()}.`
        }

        // Default message
        if (isTransactionCapped) {
            return 'This route is temporarily disabled'
        }

        return null
    }

    // Determine if there's a cap-related issue
    const hasCapIssue =
        isFeatureEnabled('TRANSACTION_CAP') && (isTransactionCapped || buyAmountExceedsCap || !!transactionCapError)

    // Determine if there's cap-related info to show (including non-error info)
    const hasCapInfo = hasCapIssue || (transactionCapRemainingAllowance !== null && timeRemaining !== null)

    const disabled =
        errors.isInsufficientBalance ||
        errors.isQuoteInvalid ||
        (errors.isBuyOutputLoading && sellData.value > BigInt(0)) ||
        sellAmountString === '0' ||
        (isFeatureEnabled('TRANSACTION_CAP') && isTransactionCapped) ||
        (isFeatureEnabled('TRANSACTION_CAP') && buyAmountExceedsCap) ||
        (isFeatureEnabled('TRANSACTION_CAP') && !!transactionCapError) ||
        (milesStatus.requiresMiles && !milesStatus.hasEnoughMiles)

    const getMilesMessage = () => {
        if (milesStatus.requiresMiles) {
            if (milesStatus.isLoadingMiles || milesStatus.isLoadingRequiredMiles) {
                return 'Checking Mach Miles requirement...'
            }
            if (milesStatus.error) {
                return `Error checking Mach Miles: ${milesStatus.error}`
            }
            if (!milesStatus.hasEnoughMiles) {
                const requiredMilesDisplay = milesStatus.requiredMiles || MIN_MILES_FOR_MON_PURCHASE
                return (
                    <span>
                        You need {requiredMilesDisplay} Miles to use MON/MEGA{' - '}
                        <a
                            href={APP}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center text-yellow-300 hover:text-yellow-200'
                            title='Visit Mach Exchange to earn Mach Miles'
                        >
                            <span className='underline'>Get Mach Miles</span>
                            <svg className='ml-1 h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                />
                            </svg>
                        </a>
                        <span className='ml-1 cursor-help text-yellow-300' data-tooltip-id='miles-explanation-tooltip'>
                            <svg className='inline h-4 w-4' fill='currentColor' viewBox='0 0 20 20'>
                                <path
                                    fillRule='evenodd'
                                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z'
                                    clipRule='evenodd'
                                />
                            </svg>
                            <Tooltip
                                id='miles-explanation-tooltip'
                                place='top'
                                className='!z-50 max-w-[300px] !bg-[#141618] !opacity-100'
                                content="Miles are Mach's version of points. Simply swap on Mach Mainnet (app.mach.exchange) and check the miles tab to see your miles balance. You'll hit the requirement fast!"
                                clickable={true}
                            />
                        </span>
                    </span>
                )
            }
        }
        return null
    }

    return (
        <>
            {isFeatureEnabled('TRANSACTION_CAP') && hasCapInfo && (
                <div
                    className={`mb-2 text-center text-sm ${
                        transactionCapError
                            ? 'text-yellow-500'
                            : isTransactionCapped || buyAmountExceedsCap
                              ? 'text-red-500'
                              : 'text-blue-500'
                    }`}
                >
                    {getTransactionCapMessage()}
                </div>
            )}
            {milesStatus.requiresMiles && (
                <div className='mb-2 text-center text-sm text-yellow-300'>{getMilesMessage()}</div>
            )}
            <SwapButton
                className={cn('w-full')}
                onClick={onConfirmExchange}
                disabled={disabled}
                errorsJSON={{
                    isInsufficientBalance: errors.isInsufficientBalance,
                    isQuoteInvalid: errors.isQuoteInvalid,
                    isBuyOutputLoading: errors.isBuyOutputLoading && Number(sellData.value) > 0,
                    hasInsufficientGas,
                    isTransactionCapped: isFeatureEnabled('TRANSACTION_CAP') && hasCapIssue, // Use the boolean hasCapIssue flag
                }}
                hasInsufficientGas={hasInsufficientGas}
                sellData={sellData}
                buyData={buyData}
                sellAmountString={sellAmountString}
                currentStep={StepNames.UNSTARTED}
                quoteResponse={quoteResponse}
                isCustomWallet={isCustomWallet}
                destinationAddress={destinationAddress}
                gasFeeSimulationInProgress={gasFeeSimulationInProgress}
                isQuoteLoading={isQuoteLoading}
                buttonText={getButtonText()} // Custom button text based on cap status
            />
        </>
    )
}
